import { useEffect, useState } from 'react'
import { useExtensionEnrollment } from '../hooks/useExtensionEnrollment'

/**
 * ExtensionBridge
 *
 * Renders nothing. Its whole job is to make the extension handshake happen on
 * ANY page of the app, for any signed-in user.
 *
 * Before this, `useExtensionEnrollment` was reachable only through
 * ContainerMonitorModal, which is mounted solely by DispatchConsole. So an
 * operator who opened their PC, went to ealoom, and worked out of the orders
 * or invoices screens all morning never triggered a handshake at all — and if
 * their token had expired overnight, container monitoring stayed dark until
 * they happened to open the dispatch console.
 *
 * Mounted once, near the top of the tree, so "open the site → the extension
 * connects" holds no matter which page they land on.
 */
export function ExtensionBridge() {
  // The hook pings the extension and re-enrols when needed. Doing that on the
  // marketing/login pages would be pointless (no session to enrol with), so
  // gate on a token being present and re-check when it appears after login.
  const [hasSession, setHasSession] = useState(() => !!localStorage.getItem('token'))

  useEffect(() => {
    if (hasSession) return
    // Login writes the token to localStorage; a same-tab write fires no
    // 'storage' event, so poll briefly rather than miss the transition.
    const timer = setInterval(() => {
      if (localStorage.getItem('token')) setHasSession(true)
    }, 2000)
    return () => clearInterval(timer)
  }, [hasSession])

  if (!hasSession) return null
  return <ExtensionBridgeActive />
}

/**
 * Split out so the hook only ever runs with a session present — hooks can't be
 * called conditionally, and we don't want its ping/enrol cycle firing on the
 * public pages.
 */
function ExtensionBridgeActive() {
  useExtensionEnrollment()
  return null
}

export default ExtensionBridge
